// Central axios instance for authenticated API calls.
//
// - Every request automatically gets `authorization: Bearer <accessToken>`
//   from localStorage.
// - On a 401 response, it calls POST /auth/refresh-token (using the
//   *expired* accessToken as the Authorization header + the stored
//   refreshToken in the body, per the API's contract), stores the new
//   accessToken/refreshToken, and retries the original request once.
// - If the refresh call itself fails (refreshToken expired/invalid), the
//   session is cleared and the user is sent to /login.
// - Concurrent 401s only trigger a single refresh call; the rest wait for
//   it and retry with the new token once it resolves.
//
// Views should import this instance instead of 'axios' directly for any
// call that requires the user to be logged in (i.e. everything except
// /auth/signin). /auth/signin needs no token so plain axios is fine there.

import axios from 'axios'
import router from '@/router'

// In local dev, vue.config.js's devServer.proxy forwards relative
// '/api' and '/auth' calls to the real backend — but that proxy only
// exists inside `vue-cli-service serve` and has NO effect on the
// production build. Once deployed (e.g. Firebase Hosting), a relative
// path resolves against the hosting domain itself, and Firebase's SPA
// fallback rewrite ("**" -> /index.html) returns index.html instead of
// JSON. So the production build needs an explicit absolute baseURL.
const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL || 'https://devcoldchain.sgroupinnovation.com'

// Plain axios instance for the refresh call itself, so it is never
// intercepted by the 401 handler below (that would recurse forever).
const rawAxios = axios.create({ baseURL: API_BASE_URL })

const http = axios.create({ baseURL: API_BASE_URL })

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers = config.headers || {}
    config.headers.authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let pendingQueue = [] // { resolve, reject } waiting on the in-flight refresh

function resolveQueue(error, newAccessToken) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(newAccessToken)
  })
  pendingQueue = []
}

function clearSessionAndRedirect() {
  localStorage.clear()
  if (router.currentRoute.value.name !== 'Login') {
    router.push('/login')
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config: originalRequest } = error

    // Only handle "expired accessToken" (401) exactly once per request.
    if (!response || response.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error)
    }

    const accessToken = localStorage.getItem('accessToken')
    const refreshToken = localStorage.getItem('refreshToken')
    if (!accessToken || !refreshToken) {
      clearSessionAndRedirect()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Another request already triggered a refresh — wait for it.
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject })
      }).then((newAccessToken) => {
        originalRequest._retry = true
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.authorization = `Bearer ${newAccessToken}`
        return http(originalRequest)
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const { data } = await rawAxios.post(
        '/auth/refresh-token',
        { refreshToken },
        { headers: { authorization: `Bearer ${accessToken}` } }
      )

      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)

      resolveQueue(null, data.accessToken)

      originalRequest.headers = originalRequest.headers || {}
      originalRequest.headers.authorization = `Bearer ${data.accessToken}`
      return http(originalRequest)
    } catch (refreshError) {
      // refreshToken expired/invalid -> can't refresh -> go to login.
      resolveQueue(refreshError, null)
      clearSessionAndRedirect()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default http
